// Phase A Round 2 — Notary Destination Deep Content
// Mounted on /notary/to/$country and /en/notary/to/$country to add
// regulatory 2025-2026 update, common pitfalls per legalisation route,
// anonymised case study, and cross-service bridges. Kept dependency-free
// on dest schema (uses countryTh/countryEn + legalisationRoute only) so it
// works across both TH + EN datasets.

import { Link } from "@tanstack/react-router";
import { AlertTriangle, Landmark, BookOpen, ShieldCheck, ArrowRight, Globe2 } from "lucide-react";

interface Props {
  countryTh: string;
  countryEn: string;
  route: "apostille" | "consular" | string;
  lang: "th" | "en";
}

const REG_TH =
  "ตั้งแต่ 15 ธันวาคม 2024 ประเทศไทยเป็นภาคี Hague Apostille Convention เต็มรูปแบบ — เอกสาร Notary ที่รับรองในไทยและมุ่งใช้ใน 130 ประเทศภาคี Apostille ไม่ต้องผ่านสถานทูตอีกต่อไป ใช้เพียง MFA Apostille ฉบับเดียว ประหยัดเวลา 5-14 วัน และค่าใช้จ่ายเฉลี่ย 1,500-3,500 บาทต่อชุด สำหรับประเทศ non-Hague (จีนแผ่นดินใหญ่ ยกเว้นฮ่องกง/มาเก๊า, UAE, ซาอุดีอาระเบีย ฯลฯ) ยังคงใช้เส้นทาง MFA + Embassy ตามเดิม";
const REG_EN =
  "Effective 15 December 2024, Thailand joined the Hague Apostille Convention as a full member. Documents notarised in Thailand for use in any of the 126 Convention states no longer require embassy legalisation — a single MFA Apostille is sufficient. This saves 5-14 days of processing and roughly THB 1,500-3,500 per document set. For non-Hague destinations (Mainland China excluding HK/Macau, UAE, Saudi Arabia, etc.) the classic MFA + Embassy chain still applies.";

const MISTAKES_APOSTILLE = [
  { th: "ส่ง Notary ไปสถานทูตแบบเดิมทั้งที่ปลายทางเข้า Apostille แล้ว — เสียเวลา 2 สัปดาห์และค่าธรรมเนียมสถานทูตโดยไม่จำเป็น", en: "Sending the notarised doc to the embassy when the destination now accepts Apostille — 2 weeks and embassy fees wasted." },
  { th: "แปลก่อน Notary แล้วส่ง MFA ทำให้ตราประทับ Apostille ครอบเฉพาะคำแปล ไม่ครอบต้นฉบับ", en: "Translating before notarisation so the Apostille only covers the translation, not the source document." },
  { th: "ใช้ e-Apostille กับหน่วยงานที่ยังยอมรับเฉพาะ paper Apostille (ยุโรปตะวันออก, อเมริกาใต้บางประเทศ)", en: "Using e-Apostille where the receiving authority still accepts paper Apostille only (parts of Eastern Europe & Latin America)." },
];
const MISTAKES_CONSULAR = [
  { th: "ข้ามขั้น MFA ไปสถานทูตโดยตรง — สถานทูตปฏิเสธทันทีเพราะไม่มีตรากระทรวงการต่างประเทศไทยรับรองก่อน", en: "Skipping MFA and going straight to the embassy — the embassy rejects on the spot because MFA endorsement is missing." },
  { th: "Notary ลายเซ็นก่อน แต่สถานทูตต้องการ Notary ตัวเอกสาร (True Copy) — ต้องเริ่มใหม่ทั้งชุด", en: "Notarising the signature when the embassy required a True Copy notarisation — the entire chain must restart." },
  { th: "ใช้คำแปลจากผู้ที่ไม่ได้รับการรับรองโดยสถานทูตปลายทาง (บางประเทศบังคับ sworn/certified list เท่านั้น)", en: "Using a translator not listed by the destination embassy — some embassies require translators from their approved sworn list only." },
];

const CASE_TH =
  "ลูกค้าชาวไทยเตรียมยื่นเปิดบริษัทที่ประเทศปลายทางในเดือนตุลาคม 2025 นำหนังสือมอบอำนาจและ Company Profile มาที่ NYC ลาดพร้าว 95 เวลา 10:00 — Notary แล้วเสร็จภายในวัน ส่ง MFA เช้าถัดไป รับ Apostille กลับภายใน 48 ชั่วโมง รวมค่าใช้จ่ายทั้งสิ้น 4,200 บาท และส่ง DHL ถึงปลายทางในวันที่ 4 — เร็วกว่าเส้นทางสถานทูตแบบเดิมประมาณ 9 วันทำการ";
const CASE_EN =
  "A Thai client preparing to incorporate a subsidiary abroad in October 2025 walked into our Lat Phrao 95 office at 10:00 with a Power of Attorney and a Company Profile. Notarisation completed same-day, MFA submitted the next morning, Apostille returned within 48 hours. Total cost THB 4,200 and DHL delivery landed on day four — roughly 9 business days faster than the pre-Apostille embassy route.";

const BRIDGES = [
  {
    hrefTh: "/apostille" as const,
    hrefEn: "/en/apostille" as const,
    toTh: "MFA Apostille (Hague)",
    toEn: "MFA Apostille (Hague)",
    whyTh: "ขั้นตอนภาครัฐหลัง Notary — จำเป็นสำหรับปลายทาง Hague 130 ประเทศ",
    whyEn: "The mandatory government step after Notary for all 126 Hague destinations.",
  },
  {
    hrefTh: "/embassy-legalization" as const,
    hrefEn: "/en/embassy-legalization" as const,
    toTh: "รับรองสถานทูต (Non-Hague)",
    toEn: "Embassy Legalisation (Non-Hague)",
    whyTh: "สำหรับจีน UAE ซาอุฯ และประเทศ non-Hague ที่ยังต้องผ่านสถานทูต",
    whyEn: "For China, UAE, Saudi and other non-Hague destinations still requiring embassy sign-off.",
  },
  {
    hrefTh: "/sworn-translator" as const,
    hrefEn: "/en/sworn-translator" as const,
    toTh: "แปลรับรองโดยผู้แปลขึ้นทะเบียน",
    toEn: "Sworn / Certified Translator",
    whyTh: "หลายประเทศบังคับใช้ผู้แปลจากรายชื่อสถานทูต — เราจัดการให้ครบ",
    whyEn: "Many destinations mandate sworn/embassy-listed translators — we handle the full list.",
  },
  {
    hrefTh: "/certify-government-documents" as const,
    hrefEn: "/en/certify-government-documents" as const,
    toTh: "ขอเอกสารราชการต้นฉบับ",
    toEn: "Government Document Retrieval",
    whyTh: "หากยังไม่มีต้นฉบับ (ทะเบียนบ้าน, สูติบัตร, ทะเบียนสมรส) เราขอให้ได้",
    whyEn: "Missing the source document (Tabien Baan, birth cert, marriage cert)? We retrieve it first.",
  },
];

export default function NotaryDestinationDeepContent({ countryTh, countryEn, route, lang }: Props) {
  const isTh = lang === "th";
  const country = isTh ? countryTh : countryEn;
  const mistakes = route === "apostille" ? MISTAKES_APOSTILLE : MISTAKES_CONSULAR;

  return (
    <div className="mx-auto max-w-5xl px-4 pb-16 sm:px-6" data-speakable>
      {/* Regulatory 2025-2026 */}
      <section className="mt-14 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
        <div className="flex items-start gap-3">
          <Landmark className="mt-1 h-6 w-6 shrink-0 text-amber-700" />
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-widest text-amber-700">
              {isTh ? "อัปเดตกฎหมาย 2025-2026" : "Regulatory update 2025-2026"}
            </p>
            <h2 className="mt-1 font-display text-xl font-extrabold text-navy-deep sm:text-2xl">
              {isTh ? `ผลต่อเอกสารที่ใช้ใน${country}` : `What this means for documents used in ${country}`}
            </h2>
            <p className="mt-3 text-[14px] leading-relaxed text-slate-700 sm:text-[15px]">
              {isTh ? REG_TH : REG_EN}
            </p>
          </div>
        </div>
      </section>

      {/* Common mistakes */}
      <section className="mt-10 rounded-3xl border border-rose-200 bg-rose-50/40 p-6 sm:p-8">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-1 h-6 w-6 shrink-0 text-rose-600" />
          <div className="min-w-0">
            <h2 className="font-display text-xl font-extrabold text-navy-deep sm:text-2xl">
              {isTh
                ? `ข้อผิดพลาดที่พบบ่อยเมื่อยื่นเอกสารไป${country}`
                : `Common pitfalls when submitting to ${country}`}
            </h2>
            <ul className="mt-4 grid gap-2.5">
              {mistakes.map((m, i) => (
                <li key={i} className="flex items-start gap-2 text-[14px] leading-relaxed text-slate-700">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-rose-500" />
                  <span>{isTh ? m.th : m.en}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Micro case study */}
      <section className="mt-10 rounded-3xl border border-slate-200 bg-slate-50/60 p-6 sm:p-8">
        <div className="flex items-start gap-3">
          <BookOpen className="mt-1 h-6 w-6 shrink-0 text-navy-deep" />
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
              {isTh ? "เคสตัวอย่างจริง (Anonymised)" : "Real client case (anonymised)"}
            </p>
            <p className="mt-2 text-[14px] italic leading-relaxed text-slate-700 sm:text-[15px]">
              "{isTh ? CASE_TH : CASE_EN}"
            </p>
          </div>
        </div>
      </section>

      {/* Cross-service bridges */}
      <section className="mt-10">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
          {isTh ? "บริการที่ควรทำต่อเนื่อง" : "Recommended next-step services"}
        </h2>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {BRIDGES.map((b) => (
            <Link
              key={b.hrefTh}
              to={(isTh ? b.hrefTh : b.hrefEn) as never}
              className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-amber-300 hover:shadow-md"
            >
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-700">
                  {isTh ? b.toTh : b.toEn}
                  <ArrowRight className="h-3.5 w-3.5 opacity-60 transition group-hover:translate-x-0.5" />
                </p>
                <p className="mt-1 text-[12px] leading-relaxed text-slate-600">
                  {isTh ? b.whyTh : b.whyEn}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Quotable NYC stat strip (AEO) */}
      <section className="mt-10 rounded-2xl border border-navy-deep/20 bg-navy-deep p-5 text-white sm:p-6">
        <Globe2 className="mb-2 h-5 w-5 text-gold" />
        <p className="text-[13px] leading-relaxed text-white/90 sm:text-sm">
          {isTh
            ? `NYC Legal & Notary Service — Notary + ${route === "apostille" ? "MFA Apostille" : "MFA + สถานทูต"} สำหรับใช้ใน${country} ดำเนินการโดยทนายผู้ทำคำรับรอง (Notarial Services Attorney) ขึ้นทะเบียนสภาทนายความแห่งประเทศไทย สำนักงานลาดพร้าว 95 กรุงเทพฯ • DBD 0435567000061 • LINE @nycli • 083-249-4999`
            : `NYC Legal & Notary Service — Notary + ${route === "apostille" ? "MFA Apostille" : "MFA + Embassy"} for documents used in ${country}, performed in person by a Notarial Services Attorney registered with the Lawyers Council of Thailand at our Lat Phrao 95, Bangkok office • DBD 0435567000061 • LINE @nycli • +66 83-249-4999.`}
        </p>
      </section>
    </div>
  );
}
